import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  LayoutDashboard,
  Newspaper,
  Calendar,
  Users,
  Building2,
  Stethoscope,
  CalendarCheck,
  MessageSquare,
  Image,
  Home,
  Quote,
  Bell,
  Settings,
  Search,
  Activity,
  Shield,
  LogOut,
  Menu,
  X,
  ChevronDown
} from 'lucide-react';
import { Button } from '../../components/ui/button';

const menuItems = [
  { icon: LayoutDashboard, label: 'Dashboard', path: '/admin/dashboard' },
  { icon: Newspaper, label: 'News', path: '/admin/news' },
  { icon: Calendar, label: 'Events', path: '/admin/events' },
  { icon: Stethoscope, label: 'Doctors', path: '/admin/doctors' },
  { icon: Building2, label: 'Departments', path: '/admin/departments' },
  { icon: CalendarCheck, label: 'Bookings', path: '/admin/bookings' },
  { icon: MessageSquare, label: 'Messages', path: '/admin/messages' },
  { icon: Image, label: 'Gallery', path: '/admin/gallery' },
  { icon: Home, label: 'Homepage', path: '/admin/homepage' },
  { icon: Quote, label: 'Testimonials', path: '/admin/testimonials' },
  { icon: Bell, label: 'Announcements', path: '/admin/announcements' },
  { icon: Search, label: 'SEO Settings', path: '/admin/seo' },
  { icon: Shield, label: 'Admin Users', path: '/admin/users' },
  { icon: Activity, label: 'Activity Logs', path: '/admin/logs' },
  { icon: Settings, label: 'Settings', path: '/admin/settings' },
];

const AdminLayout = ({ children }) => {
  const { admin, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'super_admin': return 'bg-red-100 text-red-700';
      case 'content_admin': return 'bg-blue-100 text-blue-700';
      case 'booking_officer': return 'bg-green-100 text-green-700';
      case 'doctor_manager': return 'bg-purple-100 text-purple-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  const formatRole = (role) => {
    return role?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-primary transform transition-transform duration-300 lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        {/* Logo */}
        <div className="flex items-center justify-between h-16 px-4 border-b border-white/10">
          <Link to="/admin/dashboard" className="flex items-center gap-3">
            <img 
              src="https://customer-assets.emergentagent.com/job_43b6699e-7b3c-4d06-ad8e-7888613bee79/artifacts/5oi0txpb_Logooooo%20BH%20.png"
              alt="Logo"
              className="w-10 h-10 object-contain"
            />
            <div>
              <span className="text-white font-semibold text-sm">Baobab Hospital</span>
              <span className="text-white/60 text-xs block">Admin Panel</span>
            </div>
          </Link>
          <button 
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden text-white"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="p-4 space-y-1 h-[calc(100vh-4rem)] overflow-y-auto">
          {menuItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                  isActive 
                    ? 'bg-white text-primary font-medium' 
                    : 'text-white/80 hover:bg-white/10 hover:text-white'
                }`}
              >
                <item.icon className="w-5 h-5" />
                {item.label}
              </Link>
            );
          })}
          
          <div className="pt-4 mt-4 border-t border-white/10">
            <Link
              to="/"
              target="_blank"
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-white/80 hover:bg-white/10 hover:text-white"
            >
              <Home className="w-5 h-5" />
              View Website
            </Link>
          </div>
        </nav>
      </aside>

      {/* Main Content */}
      <div className="lg:pl-64">
        {/* Top Bar */}
        <header className="sticky top-0 z-40 bg-white border-b border-slate-200 h-16 flex items-center justify-between px-4 lg:px-6">
          <button 
            onClick={() => setSidebarOpen(true)}
            className="lg:hidden text-slate-700"
          >
            <Menu className="w-6 h-6" />
          </button>

          <div className="flex items-center gap-4 ml-auto">
            {/* Admin Info */}
            <div className="flex items-center gap-3">
              <div className="text-right hidden sm:block">
                <p className="text-sm font-medium text-slate-700">{admin?.name}</p>
                <span className={`text-xs px-2 py-0.5 rounded-full ${getRoleBadgeColor(admin?.role)}`}>
                  {formatRole(admin?.role)}
                </span>
              </div>
              <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center text-white font-semibold">
                {admin?.name?.charAt(0)}
              </div>
            </div>

            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleLogout}
              className="text-red-600 hover:text-red-700 hover:bg-red-50"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-4 lg:p-6">
          {children}
        </main>
      </div>

      {/* Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default AdminLayout;
